import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Bars3Icon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';

import { Button } from '@/components/ui/catalyst/button';
import { Input } from '@/components/ui/catalyst/input';
import {
  AgentChatWorkspaceExhibit,
  ExhibitLabelFormat,
} from '@/services/types/agent-chat-workspace-types';

/**
 * Convert a 0-based index to an alphabetic label (Excel-style columns).
 * 0 -> "A", 25 -> "Z", 26 -> "AA", 27 -> "AB", etc.
 */
function indexToAlphabeticLabel(index: number): string {
  let result = '';
  let i = index;
  while (true) {
    const remainder = i % 26;
    result = String.fromCharCode('A'.charCodeAt(0) + remainder) + result;
    i = Math.floor(i / 26);
    if (i === 0) break;
    i -= 1; // Shift for 1-based carry like Excel columns
  }
  return result;
}

/**
 * Convert a 0-based index to the appropriate label based on format.
 */
function indexToLabel(index: number, format: ExhibitLabelFormat): string {
  if (format === ExhibitLabelFormat.NUMERIC) {
    return String(index + 1);
  }
  return indexToAlphabeticLabel(index);
}

interface ExhibitItemProps {
  exhibit: AgentChatWorkspaceExhibit;
  index: number;
  labelFormat?: ExhibitLabelFormat;
  onUpdateName: (exhibitId: string, newName: string) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onDelete: (exhibitId: string) => void;
}

export const ExhibitItem = ({
  exhibit,
  index,
  labelFormat = ExhibitLabelFormat.ALPHABETIC,
  onUpdateName,
  onMoveUp,
  onMoveDown,
  onDelete,
}: ExhibitItemProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(exhibit.name);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: exhibit.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleStartEdit = () => {
    setEditName(exhibit.name);
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    if (editName.trim() && editName !== exhibit.name) {
      onUpdateName(exhibit.id, editName.trim());
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditName(exhibit.name);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-4 p-4 hover:bg-gray-50 ${
        isDragging ? 'bg-gray-100 shadow-lg' : ''
      }`}
      {...attributes}
    >
      {/* Drag Handle */}
      <div
        {...listeners}
        className="flex h-8 w-8 shrink-0 cursor-grab items-center justify-center rounded hover:bg-gray-200 active:cursor-grabbing"
      >
        <Bars3Icon className="h-4 w-4 text-gray-400" />
      </div>

      {/* Order Number */}
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-800">
        {indexToLabel(index, labelFormat)}
      </div>

      {/* Document Icon */}
      <DocumentIcon className="h-5 w-5 shrink-0 text-gray-400" />

      {/* Exhibit Info */}
      <div className="min-w-0 flex-1">
        {isEditing ? (
          <div className="flex items-center gap-2">
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 max-w-120"
              autoFocus
            />
            <Button onClick={handleSaveEdit} className="h-8 w-8 p-0">
              <CheckIcon className="h-4 w-4" />
            </Button>
            <Button plain onClick={handleCancelEdit} className="h-8 w-8 p-0">
              <XMarkIcon className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div>
            <h4 className="truncate text-sm font-medium text-gray-900">
              {exhibit.name}
            </h4>
            <p className="text-xs text-gray-500">
              {exhibit.blob_details.original_filename || 'Untitled'} •{' '}
              {formatFileSize(exhibit.blob_details.file_size)}
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex shrink-0 items-center gap-1">
        {/* Preview PDF Button */}
        {exhibit.blob_details.download_url && (
          <Button
            plain
            onClick={() =>
              window.open(exhibit.blob_details.download_url!, '_blank')
            }
            className="h-8 w-8 p-0 opacity-50 group-hover:opacity-100 hover:bg-gray-100"
            title="Preview PDF"
          >
            <EyeIcon className="h-4 w-4" />
          </Button>
        )}
        {/* Edit Button */}
        <Button
          plain
          onClick={handleStartEdit}
          className="h-8 w-8 p-0 opacity-50 group-hover:opacity-100 hover:bg-gray-100"
          title="Edit exhibit name"
        >
          <PencilIcon className="h-4 w-4" />
        </Button>
        <div className="flex flex-col">
          <Button
            plain
            onClick={onMoveUp}
            disabled={!onMoveUp}
            className="h-5 w-6 p-0 disabled:opacity-25"
          >
            <ChevronUpIcon className="h-3 w-3" />
          </Button>
          <Button
            plain
            onClick={onMoveDown}
            disabled={!onMoveDown}
            className="h-5 w-6 p-0 disabled:opacity-25"
          >
            <ChevronDownIcon className="h-3 w-3" />
          </Button>
        </div>
        {/* Delete Button */}
        <Button
          plain
          onClick={() => onDelete(exhibit.id)}
          className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
        >
          <TrashIcon className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
